import { isSplitKeyboard } from "@/hardware/geometry";
import { builtinOrder, builtins } from "@/hardware/keyboards/builtin";
import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";

// Built-in ids split into rectangular ("Standard") and two-piece ("Split") boards, derived from
// geometry, so the picker shows the two families under separate sections. Order within
// each follows builtinOrder.
const standardBoards = builtinOrder.filter((id) => {
  const kb = builtins[id];
  return kb != null && !isSplitKeyboard(kb);
});
const splitBoards = builtinOrder.filter((id) => {
  const kb = builtins[id];
  return kb != null && isSplitKeyboard(kb);
});

interface KeyboardSelectProps {
  ariaLabel: string;
  /** The selected board id; "" selects the leading `sameAs` option when one is offered. */
  value: string;
  /** Receives a board id, or "" when the `sameAs` option is chosen. */
  onChange: (id: string) => void;
  imported: Record<string, ImportedKeyboard>;
  /** A leading "follow the other side" option (Compare B-side); omitted off-compare. */
  sameAs?: { label: string } | null;
  /** Locked when a board-bound Profile pins the lens. */
  disabled?: boolean;
}

/**
 * The Keyboard picker: built-in boards plus any imported ones. Shared by the control bar
 * and both Compare header cards (per-side boards, so A and B can differ - the diff matches
 * by physical position, which may not line up across boards). The
 * B-side adds a leading "Same as A" option.
 */
export function KeyboardSelect({
  ariaLabel,
  value,
  onChange,
  imported,
  sameAs,
  disabled,
}: KeyboardSelectProps) {
  const importedEntries = Object.values(imported);
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {sameAs ? <option value="">{sameAs.label}</option> : null}
      <optgroup label="Standard">
        {standardBoards.map((id) => (
          <option key={id} value={id}>
            {builtins[id]?.name}
          </option>
        ))}
      </optgroup>
      <optgroup label="Split">
        {splitBoards.map((id) => (
          <option key={id} value={id}>
            {builtins[id]?.name}
          </option>
        ))}
      </optgroup>
      {importedEntries.length > 0 ? (
        <optgroup label="Imported">
          {importedEntries.map((entry) => (
            <option key={entry.keyboard.id} value={entry.keyboard.id}>
              {entry.keyboard.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </select>
  );
}
